import ngTranslate from 'angular-translate';
import ngFileUpload from 'ng-file-upload';
import uniq from 'lodash/uniq';
import remove from 'lodash/remove';
import extend from 'lodash/extend';
import isArray from 'lodash/isArray';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';

import { ImageEditorModule } from 'admin/components/ImageEditor/index.ng';
import { VlnDialogControllerModule } from 'components/modals/dialog.controller';
import { ImageReaderServiceModule } from 'services/utilities/imageReader.service';
import { ImagesServiceModule } from 'services/data/images.service';
import { ModelServiceModule } from 'services/data/model/model.service';
import { ImageLibraryModule } from 'components/imageLibrary/imageLibrary.module';

import { CameraIconModule } from './components/cameraIcon.component';
import { ImageScrollerModule } from './components/imageScroller.module';

import template from './imageManager.html';
import './imageManager.scss';

import metaDataModalTemplate from './components/image.metadata.modal.html';

export const ImageManagerModule = angular
    .module('app.components.imageManager', [
        ngTranslate,
        'ngMaterial',
        ngFileUpload,
        ImageEditorModule,
        VlnDialogControllerModule,
        ImageReaderServiceModule,
        ImagesServiceModule,
        ModelServiceModule,
        ImageScrollerModule,
        ImageLibraryModule,
        CameraIconModule
    ]).directive('imageManager', (
        $window,
        $translate,
        $mdToast,
        $timeout,
        $rootScope,
        modelService,
        $mdDialog,
        imageReader,
        imagesService
    ) => {
        return {
            restrict: 'E',
            template,
            scope: {
                images: '=?',
                image: '=?',
                whitelist: '=?',
                preWhitelist: '<?',
                onWhitelistChange: '&?',
                libraryOverride: '&?',
                singleMode: '=',
                galleryMode: '=',
                imageLibraryContext: '@',
                imageLibraryAssociationTags: '=',
                size: '@',
                onRemoveImageClicked: '&',
                onImageUploaded: '&',
                retrieveStorefrontInfo: '&',
                isPinterestEnabled: '=',
                bypassImageTransformation: '<'
            },
            link: (scope, ele, attrs) => {
                scope.gtmid = attrs.gtmid || null;
            },
            controller: function imageManagerController($scope) {
                this.registerScroller = registerScroller;
                let imageLimit = 2500;
                let vm = {
                    files: [],
                    singleMode: $scope.singleMode,
                    size: $scope.size || 'lg',
                    galleryMode: $scope.galleryMode,
                    croppedImage: null,
                    scroller: null,
                    cropHeight: 0,
                    cropWidth: 0,
                    preWhitelist: $scope.preWhitelist,
                    hasWhitelist: !!$scope.whitelist,
                    imageLimitReached: false,
                    isPinterestEnabled: $scope.isPinterestEnabled,
                    isImageEditModalOpen: false,
                    imageId: '',
                    imageUrl: '',
                    gtmImageSaveEventName: '',

                    onLibraryImagesSelected,
                    uploadImages,
                    uploadFile,
                    openEditor,
                    openImageMetadataModal,
                    removeImage,
                    trackDrop,
                    hasImage,
                    moveImageRight,
                    moveImageLeft,
                    shouldShowMoveImageLeft,
                    shouldShowMoveImageRight,
                    whitelistFilter,
                    preWhitelistFilter,
                    postImageToPinterest,
                    onCloseImageEditor
                };

                init();

                function init() {
                    $scope.vm = vm;
                    $scope.$on('resetImages', () => {
                        vm.scroller.resetScroll();
                    });
                    getImagesCount();
                    $scope.$on('draggableDropSuccess', resetImages);

                    if ($scope.libraryOverride) {
                        vm.libraryOverrideFunc = function() {
                            $scope.libraryOverride()();
                            $scope.$apply();
                        };
                        vm.hasLibraryOverride = true;
                    }

                    $scope.$on('imageManager:onLibrarySelected', (event, images) => {
                        onLibraryImagesSelected(images);
                    });
                }

                function resetImages() {
                    const imagesCopy = angular.copy($scope.images);
                    $scope.images = [];
                    $scope.$apply();
                    $scope.images = imagesCopy;
                    $scope.$apply();
                }

                function getImagesCount(tags = [], page = 0, count = 0) {
                    return imagesService.getImagesFromAlgolia(tags, page, count)
                        .then((data) => {
                            return data.nbHits;
                        }).then((totalCount) => {
                            imagesService.getImageLimit().then((limit) => {
                                imageLimit = limit;
                                vm.imageLimitReached = (totalCount >= imageLimit);
                            });
                        });
                }

                function onLibraryImagesSelected(selectedImages) {
                    if (!vm.singleMode) {
                        angular.forEach(selectedImages, (image) => {
                            let newImage = {
                                imageLink: image
                            };
                            if (!isAlreadyInImages(newImage)) {
                                $scope.images.push(modelService.makeModel(newImage, 'imageMetadataModel'));
                            }
                            if ($scope.whitelist) {
                                $scope.whitelist.push(newImage.imageLink.id);
                                $scope.whitelist = uniq($scope.whitelist);
                                $scope.onWhitelistChange()($scope.whitelist);
                                $scope.$emit('imageManager:onLibrarySelectedComplete');
                            }
                        });
                    } else {
                        let newImage = {
                            imageLink: selectedImages
                        };
                        angular.extend($scope.image, newImage);
                    }
                    $scope.onImageUploaded();
                }

                function isAlreadyInImages(newImage) {
                    const existingImageIds = $scope.images.map((img) => {
                        return img.imageLink.id;
                    });
                    return existingImageIds.indexOf(newImage.imageLink.id) !== -1;
                }

                function registerScroller(scroller) {
                    vm.scroller = scroller;
                }

                function uploadImages(files, rejected) {
                    if (vm.imageLimitReached) {
                        $translate('ERROR.IMAGELIMITREACHED').then((error) => {
                            $mdToast.show($mdToast.simple()
                                .textContent(error)
                                .position('bottom left')
                            );
                        });
                        return;
                    }

                    if (rejected && rejected.length) {
                        $translate('ERROR.IMAGEVALIDATION').then((error) => {
                            $mdToast.show($mdToast.simple()
                                .textContent(error)
                                .position('bottom left')
                            );
                        });
                    }

                    if (!files) {
                        return;
                    }

                    vm.files = vm.files.concat(files);
                    files.forEach(file => vm.uploadFile(file));
                    $window.dataLayer.push({
                        event: `${$scope.gtmid}-uploadImage`
                    });
                }

                function uploadFile(file, idx) {
                    file.progress = 0;
                    imageReader.getImageData(file)
                        .then((data) => {
                            return imagesService.saveImage(data);
                        }, () => {
                            remove(vm.files, file);
                        }, (completedPercentage) => {
                            file.progress = completedPercentage;
                        })
                        .then((response) => {
                            $scope.onImageUploaded();
                            remove(vm.files, file);
                            let newImage = {
                                imageLink: response
                            };

                            if (vm.singleMode) {
                                $scope.image = modelService.makeModel(newImage, 'imageMetadataModel');
                            } else {
                                if (idx != null) {
                                    newImage = extend({}, $scope.images[idx], newImage);
                                    $scope.images.splice(
                                        idx,
                                        1,
                                        modelService.makeModel(newImage, 'imageMetadataModel')
                                    );
                                } else {
                                    $scope.images.push(modelService.makeModel(newImage, 'imageMetadataModel'));
                                }
                            }
                            if ($scope.whitelist) {
                                $scope.whitelist.push(newImage.imageLink.id);
                                $scope.whitelist = uniq($scope.whitelist);
                                $scope.onWhitelistChange()($scope.whitelist);
                            }
                            vm.isImageEditModalOpen = false;
                            $mdToast.show($mdToast.simple()
                                .textContent('Image edited successfully')
                                .position('bottom left')
                            );
                            $window.dataLayer.push({
                                event: `${$scope.gtmid}-editImage`
                            });
                        })
                        .catch(() => {
                            remove(vm.files, file);
                            $translate('ERROR.GENERAL_FAILURE_ERROR').then((error) => {
                                $mdToast.show($mdToast.simple()
                                    .textContent(error)
                                    .position('bottom left')
                                );
                            });
                        });
                }

                function openImageMetadataModal(image) {
                    $window.dataLayer.push({
                        event: `${$scope.gtmid}-imageMetadata`
                    });
                    vm.selectedImage = angular.copy(image);

                    let dialogOptions = {
                        clickOutsideToClose: true,
                        template: metaDataModalTemplate,
                        scope: $scope,
                        preserveScope: true,
                        controller: 'vlnDialogController'
                    };

                    return $mdDialog.show(dialogOptions)
                        .then(() => {
                            if (vm.singleMode) {
                                $scope.image = vm.selectedImage;
                            } else {
                                let idx = $scope.images.indexOf(image);
                                $scope.images.splice(idx, 1, vm.selectedImage);
                            }
                        });
                }

                function removeImage(image) {
                    if ($scope.whitelist) {
                        removeFromWhitelist(image.imageLink.id);
                        return;
                    }

                    $rootScope.$broadcast('imageManager:imageRemoved');

                    $window.dataLayer.push({
                        event: `${$scope.gtmid}-removeImage`
                    });

                    if (vm.singleMode) {
                        $scope.onRemoveImageClicked(image);
                    } else {
                        let idx = $scope.images.indexOf(image);
                        $scope.images.splice(idx, 1);
                    }

                    $timeout(() => {
                        vm.scroller.moveIntoPosition();
                    });
                }

                function removeFromWhitelist(imageLinkId) {
                    const index = $scope.whitelist.indexOf(imageLinkId);
                    if (index !== -1) {
                        $scope.whitelist.splice(index, 1);
                        $scope.onWhitelistChange()($scope.whitelist);
                    }
                }

                function trackDrop() {
                    $window.dataLayer.push({
                        event: `${$scope.gtmid}-dropImage`
                    });
                }

                function openEditor(image) {
                    vm.selectedImage = image;
                    let idx = isArray($scope.images)
                        ? findIndex($scope.images, img => isEqual(img, vm.selectedImage))
                        : -1;

                    vm.isImageEditModalOpen = true;
                    vm.imageId = image.imageLink.id;
                    vm.imageUrl = image.imageLink.fullUri;
                    vm.onSave = vm.uploadFile;
                    vm.imageIndex = idx;
                }

                function onCloseImageEditor() {
                    vm.isImageEditModalOpen = false;
                    $scope.$apply();
                }

                function hasImage() {
                    if (vm.singleMode) {
                        return $scope.image && $scope.image.imageLink;
                    }

                    return $scope.images && $scope.images.length;
                }

                function moveImageRight(index) {
                    if (!shouldShowMoveImageRight(index)) return;
                    moveItem(index, index + 1, $scope.images);
                }

                function moveImageLeft(index) {
                    if (!shouldShowMoveImageLeft(index)) return;
                    moveItem(index, index - 1, $scope.images);
                }

                function moveItem(from, to, items) {
                    items.splice(to, 0, $scope.images.splice(from, 1)[0]);
                }

                function shouldShowMoveImageLeft(index) {
                    return $scope.images.length > 1 && index !== 0;
                }

                function shouldShowMoveImageRight(index) {
                    return $scope.images.length > 1 && index !== $scope.images.length - 1;
                }

                function whitelistFilter(item) {
                    if ($scope.whitelist) {
                        return $scope.whitelist.indexOf(item.imageLink.id) !== -1;
                    }
                    return true;
                }

                function preWhitelistFilter(item) {
                    if ($scope.preWhitelist) {
                        return $scope.preWhitelist.indexOf(item.imageLink.id) !== -1;
                    }
                    return true;
                }

                // (image, storefrontInfo) -> { media, url, description }
                function _createPin(image, storefrontInfo) {
                    return {
                        media: image.imageLink.fullUri,
                        url: storefrontInfo.url,
                        description: storefrontInfo.name
                    };
                }

                // see https://developers.pinterest.com/docs/widgets/save/?
                // image -> Promise(pin)
                function postImageToPinterest(image) {
                    return $scope.retrieveStorefrontInfo()
                        .then((storefrontInfo) => {
                            const pin = _createPin(image, storefrontInfo);
                            $window.PinUtils.pinOne(pin);
                            return pin;
                        });
                }
            }
        };
    }).name;



// WEBPACK FOOTER //
// ./src/app/components/imageManager/imageManager.module.js